import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useOutletContext } from "react-router-dom";
import { Send, ImagePlus, X, Plus, MessageCircle, Siren, Sparkles, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { fileToBase64, timeAgo } from "@/lib/files";
import { Spinner } from "@/components/kit";
import UpgradeModal from "@/components/UpgradeModal";
import DogAvatar from "@/components/DogAvatar";

const SUGGESTIONS = [
  "What human foods are toxic to dogs?",
  "My dog is scratching a lot — what could it be?",
  "How much exercise does my dog need?",
  "Is it normal for my dog to eat grass?",
];

export default function Chat() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { openVerify } = useOutletContext() || {};
  const [sessions, setSessions] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [dogId, setDogId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [input, setInput] = useState("");
  const [image, setImage] = useState(null);
  const [sending, setSending] = useState(false);
  const [upgrade, setUpgrade] = useState(null); // {used, limit} when 402
  const scrollRef = useRef(null);

  const loadSidebar = useCallback(async () => {
    const [s, d] = await Promise.all([
      apiFetch("/chat/sessions").catch(() => []),
      apiFetch("/dogs").catch(() => []),
    ]);
    setSessions(s || []);
    setDogs(d || []);
    if (d?.length && !dogId) setDogId(d[0].id);
  }, [dogId]);

  useEffect(() => {
    loadSidebar();
  }, []);

  useEffect(() => {
    (async () => {
      if (!sessionId) {
        setMessages([]);
        return;
      }
      setLoadingMsgs(true);
      try {
        const msgs = await apiFetch(`/chat/sessions/${sessionId}/messages`);
        setMessages(msgs || []);
      } catch {
        toast.error("Could not load conversation");
        navigate("/app/chat", { replace: true });
      } finally {
        setLoadingMsgs(false);
      }
    })();
  }, [sessionId, navigate]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(await fileToBase64(file));
    e.target.value = "";
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if ((!text && !image) || sending) return;
    setSending(true);
    setInput("");
    const sentImage = image;
    setImage(null);
    try {
      const res = await apiFetch("/chat/send", {
        method: "POST",
        body: JSON.stringify({
          session_id: sessionId || null,
          dog_id: dogId,
          text,
          image_base64: sentImage,
        }),
      });
      setMessages((m) => [...m, res.user_message, res.assistant_message]);
      if (!sessionId) {
        navigate(`/app/chat/${res.session_id}`, { replace: true });
      }
      loadSidebar();
      if (res.free_remaining === 0) {
        setUpgrade({ soft: true, used: res.free_used, limit: res.free_limit });
      }
    } catch (err) {
      if (err.status === 402) {
        setUpgrade({ used: err.detail?.used, limit: err.detail?.limit });
      } else if (err.status === 403 && err.detail?.code === "email_not_verified") {
        toast.error("Please verify your email to use AI chat.");
        openVerify?.();
      } else if (err.status === 429) {
        toast.error(err.detail?.message || "You're sending messages too quickly. Please slow down.");
      } else {
        toast.error(err.message || "Something went wrong");
      }
      if (text) setInput(text);
      setImage(sentImage);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-5 h-[calc(100vh-9rem)]">
      {/* Sessions sidebar */}
      <aside className={`${sessionId ? "hidden md:flex" : "flex"} flex-col rounded-3xl border border-[#E8E3DA] bg-white overflow-hidden`}>
        <div className="p-4 border-b border-[#E8E3DA]">
          <button
            data-testid="chat-new-btn"
            onClick={() => navigate("/app/chat")}
            className="w-full flex items-center justify-center gap-2 rounded-full bg-[#E86A45] text-white font-extrabold py-2.5 hover:bg-[#d65f3c] transition-colors"
          >
            <Plus size={18} /> New chat
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {sessions.length === 0 ? (
            <p className="text-sm text-[#9A938B] text-center p-6">No conversations yet.</p>
          ) : (
            sessions.map((s) => (
              <Link
                key={s.id}
                to={`/app/chat/${s.id}`}
                data-testid={`chat-session-${s.id}`}
                className={`flex items-center gap-3 p-3 rounded-2xl mb-1 transition-colors ${
                  s.id === sessionId ? "bg-[#F6DED5]" : "hover:bg-[#F2EFE9]"
                }`}
              >
                {s.is_emergency ? <Siren size={16} color="#D9534F" /> : <MessageCircle size={16} color="#E86A45" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold truncate">{s.title}</p>
                  <p className="text-xs text-[#9A938B] truncate">{timeAgo(s.updated_at)}</p>
                </div>
              </Link>
            ))
          )}
        </div>
      </aside>

      {/* Conversation */}
      <section className="flex flex-col rounded-3xl border border-[#E8E3DA] bg-white overflow-hidden">
        {/* Dog selector header */}
        <div className="flex items-center justify-between gap-3 p-4 border-b border-[#E8E3DA]">
          <h2 className="font-display font-extrabold text-lg flex items-center gap-2">
            <Sparkles size={18} color="#E86A45" /> Ask AI
          </h2>
          {dogs.length > 0 && (
            <div className="relative flex items-center gap-2">
              <DogAvatar dog={dogs.find((d) => d.id === dogId)} size={30} />
              <div className="relative">
                <select
                  data-testid="chat-dog-select"
                  value={dogId || ""}
                  onChange={(e) => setDogId(e.target.value)}
                  className="appearance-none text-sm font-bold bg-[#F2EFE9] rounded-full pl-4 pr-9 py-2 outline-none cursor-pointer"
                >
                  {dogs.map((d) => (
                    <option key={d.id} value={d.id}>About {d.name}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" color="#4A4746" />
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
          {loadingMsgs ? (
            <div className="flex justify-center pt-10"><Spinner /></div>
          ) : messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-4">
              <div className="h-16 w-16 rounded-3xl bg-[#F6DED5] flex items-center justify-center mb-4">
                <MessageCircle size={30} color="#E86A45" />
              </div>
              <h3 className="font-display text-xl font-extrabold">Ask me anything about your dog</h3>
              <p className="text-[#4A4746] mt-1 max-w-sm">Health, behavior, nutrition, or upload a photo of a symptom.</p>
              <div className="flex flex-wrap justify-center gap-2 mt-5 max-w-md">
                {SUGGESTIONS.map((q) => (
                  <button
                    key={q}
                    data-testid="chat-suggestion"
                    onClick={() => send(q)}
                    className="text-sm px-4 py-2 rounded-full bg-[#F2EFE9] hover:bg-[#E8E3DA] font-semibold text-[#4A4746] transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m) => <Bubble key={m.id} m={m} />)
          )}
          {sending && (
            <div className="flex items-center gap-2 text-[#9A938B] pl-2">
              <span className="typing-dot" /> <span className="typing-dot" /> <span className="typing-dot" />
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="p-3 border-t border-[#E8E3DA]">
          {image && (
            <div className="relative inline-block mb-2">
              <img src={image} alt="attachment" className="h-16 w-16 rounded-xl object-cover" />
              <button
                onClick={() => setImage(null)}
                data-testid="chat-remove-image"
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#2D2B2A] text-white flex items-center justify-center"
              >
                <X size={14} />
              </button>
            </div>
          )}
          <div className="flex items-end gap-2">
            <label
              data-testid="chat-image-btn"
              className="h-11 w-11 shrink-0 rounded-full bg-[#F2EFE9] hover:bg-[#E8E3DA] flex items-center justify-center cursor-pointer transition-colors"
              title="Attach a photo"
            >
              <ImagePlus size={20} color="#E86A45" />
              <input type="file" accept="image/*" className="hidden" onChange={pickImage} />
            </label>
            <textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={1}
              placeholder="Ask about your dog's health…"
              className="flex-1 resize-none max-h-32 rounded-2xl border border-[#E8E3DA] bg-[#FCFBF8] px-4 py-3 outline-none focus:border-[#E86A45] focus:ring-2 focus:ring-[#F6DED5]"
            />
            <button
              data-testid="chat-send-btn"
              onClick={() => send()}
              disabled={sending || (!input.trim() && !image)}
              className="h-11 w-11 shrink-0 rounded-full bg-[#E86A45] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#d65f3c] transition-colors"
            >
              <Send size={20} />
            </button>
          </div>
        </div>
      </section>

      <UpgradeModal
        open={!!upgrade}
        used={upgrade?.used}
        limit={upgrade?.limit}
        soft={upgrade?.soft}
        onClose={() => setUpgrade(null)}
      />
    </div>
  );
}

function Bubble({ m }) {
  const isUser = m.role === "user";
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] bg-[#E86A45] text-white rounded-3xl rounded-br-lg px-4 py-3">
          {m.image_base64 && <img src={m.image_base64} alt="upload" className="rounded-xl mb-2 max-h-48" />}
          {m.text && <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start">
      <div
        className={`max-w-[85%] rounded-3xl rounded-bl-lg px-4 py-3 ${
          m.is_emergency ? "bg-[#FDECEB] border border-[#D9534F]" : "bg-[#F2EFE9]"
        }`}
      >
        {m.is_emergency && (
          <div className="flex items-center gap-2 mb-1.5 text-[#D9534F] font-extrabold text-sm">
            <Siren size={16} /> Possible emergency
          </div>
        )}
        <p className="whitespace-pre-wrap leading-relaxed text-[#2D2B2A]">{m.text}</p>
        {m.is_emergency && (
          <Link
            to="/app/emergency"
            data-testid="chat-emergency-link"
            className="inline-flex items-center gap-1.5 mt-2.5 rounded-full bg-[#D9534F] text-white font-bold text-sm px-4 py-2"
          >
            <Siren size={15} /> Find a 24/7 vet now
          </Link>
        )}
      </div>
    </div>
  );
}
